import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { get, post } from '@/shared/api/api-client';
import type { EmergencyContactList } from '@/types/patient-api';

/**
 * Emergency-contact service hooks for the **patient edit screen**
 * (Emergency Contacts section under PROD-729).
 *
 * Wire shape (verified against live curl 2026-05-14):
 *   GET  `/api/Patients/EmergencyContactList/<patientId>`
 *        → `EmergencyContactList[]`
 *   POST `/api/Patients/EmergencyContact?PatientId=<patientId>`
 *        body: `EmergencyContactList[]` (whole-list-on-save, not deltas)
 *        → response: server-echoed list (with assigned IDs on new rows)
 *
 * **Save is atomic over the full list.** Adding a new row + editing an
 * existing one + soft-deleting another all ship in one POST. Backend
 * tombstones the rows whose `isDeleted=true` and persists the rest.
 *
 * Cache key: `['emergency-contacts', patientId]`. The patient query
 * (`['patient', id]`) is NOT invalidated on save — emergency contacts
 * are a sibling sub-resource, not part of the patient body, so their
 * cache stays orthogonal.
 *
 * **Naming clash note:** there's also a `useSaveEmergencyContacts` in
 * `./use-save-emergency-contacts.ts` (PROD-715-era) used by the
 * scheduling inline create-appointment flow. That hook operates on the
 * scheduling `EmergencyContact` type, not the patient `EmergencyContactList`.
 * To avoid ambiguity, the new mutation here is exported as
 * `useSavePatientEmergencyContactList`.
 */

interface SaveEmergencyContactsArgs {
  patientId: string;
  contacts: EmergencyContactList[];
}

export function useEmergencyContacts(patientId: string | null | undefined) {
  return useQuery({
    queryKey: ['emergency-contacts', patientId],
    queryFn: ({ signal }) =>
      get<EmergencyContactList[]>(
        `/api/Patients/EmergencyContactList/${patientId}`,
        { signal },
      ),
    enabled: !!patientId,
  });
}

export function useSavePatientEmergencyContactList() {
  const qc = useQueryClient();
  return useMutation<EmergencyContactList[], Error, SaveEmergencyContactsArgs>({
    mutationFn: ({ patientId, contacts }) =>
      post<EmergencyContactList[]>(
        `/api/Patients/EmergencyContact?PatientId=${encodeURIComponent(patientId)}`,
        contacts,
      ),
    onSuccess: (data, vars) => {
      // Prime cache with the POST response (the server-echoed list with
      // newly assigned IDs). Matches the `useUpdatePatient` pattern —
      // see that hook's onSuccess for the rationale (call-storm fix).
      if (data) {
        qc.setQueryData(['emergency-contacts', vars.patientId], data);
      } else {
        qc.invalidateQueries({ queryKey: ['emergency-contacts', vars.patientId] });
      }
    },
  });
}
